import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import Column from '../../../../components/Column';
import SvgFragment from '../../../../fragments/SvgFragment';

import { CampaignRecipientSource } from '../../../../interfaces/models/campaign';

export type CampaignWizardLeadSource = CampaignRecipientSource;

interface ICampaignRecipientsSectionProps {
  leadSource: CampaignWizardLeadSource;
  onLeadSourceChange: (source: CampaignWizardLeadSource) => void;
}

const CampaignRecipientsSection: FunctionComponent<ICampaignRecipientsSectionProps> = ({
  leadSource,
  onLeadSourceChange,
}) => (
  <Column gap="12px">
    <Column gap="2px">
      <SectionTitle>Where should recipients come from?</SectionTitle>
      <SectionSub>
        Choose a source. You&apos;ll select or import the actual people in the next step.
      </SectionSub>
    </Column>

    <LeadSourceGrid>
      <LeadSourceCard type="button" $active={leadSource === 'leadsearch'} onClick={() => onLeadSourceChange('leadsearch')}>
        <CardTopRow>
          <IconBox $active={leadSource === 'leadsearch'}><SvgFragment type="sideLeadSearchLucide" /></IconBox>
          <RadioDot $active={leadSource === 'leadsearch'} aria-hidden>
            {leadSource === 'leadsearch' && <SvgFragment type="checkmark" />}
          </RadioDot>
        </CardTopRow>
        <LeadSourceTitle $active={leadSource === 'leadsearch'}>LeadSearch</LeadSourceTitle>
        <LeadSourceSub>Find new prospects using filters like role, company, location, and keywords.</LeadSourceSub>
      </LeadSourceCard>
      <LeadSourceCard type="button" $active={leadSource === 'contacts'} onClick={() => onLeadSourceChange('contacts')}>
        <CardTopRow>
          <IconBox $active={leadSource === 'contacts'}><SvgFragment type="userPlusLucide" /></IconBox>
          <RadioDot $active={leadSource === 'contacts'} aria-hidden>
            {leadSource === 'contacts' && <SvgFragment type="checkmark" />}
          </RadioDot>
        </CardTopRow>
        <LeadSourceTitle $active={leadSource === 'contacts'}>LeadDelta Contacts</LeadSourceTitle>
        <LeadSourceSub>Select people already saved in your LeadDelta workspace.</LeadSourceSub>
      </LeadSourceCard>
      <LeadSourceCard type="button" $active={leadSource === 'csv'} onClick={() => onLeadSourceChange('csv')}>
        <CardTopRow>
          <IconBox $active={leadSource === 'csv'}><SvgFragment type="fileSpreadsheetLucide" /></IconBox>
          <RadioDot $active={leadSource === 'csv'} aria-hidden>
            {leadSource === 'csv' && <SvgFragment type="checkmark" />}
          </RadioDot>
        </CardTopRow>
        <LeadSourceTitle $active={leadSource === 'csv'}>CSV import</LeadSourceTitle>
        <LeadSourceSub>Upload a CSV with LinkedIn profile URLs or public identifiers.</LeadSourceSub>
      </LeadSourceCard>
      <LeadSourceCard type="button" $active={leadSource === 'posts'} onClick={() => onLeadSourceChange('posts')}>
        <CardTopRow>
          <IconBox $active={leadSource === 'posts'}><SvgFragment type="messageSquareTextLucide" /></IconBox>
          <RadioDot $active={leadSource === 'posts'} aria-hidden>
            {leadSource === 'posts' && <SvgFragment type="checkmark" />}
          </RadioDot>
        </CardTopRow>
        <LeadSourceTitle $active={leadSource === 'posts'}>Post URL</LeadSourceTitle>
        <LeadSourceSub>Paste a LinkedIn post URL to import everyone who commented on it.</LeadSourceSub>
      </LeadSourceCard>
      <LeadSourceCard type="button" $active={leadSource === 'sn_lists'} onClick={() => onLeadSourceChange('sn_lists')}>
        <CardTopRow>
          <IconBox $active={leadSource === 'sn_lists'}><SvgFragment type="list" /></IconBox>
          <RadioDot $active={leadSource === 'sn_lists'} aria-hidden>
            {leadSource === 'sn_lists' && <SvgFragment type="checkmark" />}
          </RadioDot>
        </CardTopRow>
        <LeadSourceTitle $active={leadSource === 'sn_lists'}>Sales Navigator</LeadSourceTitle>
        <LeadSourceSub>Import people from a Sales Navigator lead list you own or from a Sales Navigator search URL.</LeadSourceSub>
      </LeadSourceCard>
      <LeadSourceCard type="button" $active={leadSource === 'empty'} onClick={() => onLeadSourceChange('empty')}>
        <CardTopRow>
          <IconBox $active={leadSource === 'empty'}><SvgFragment type="clipboardListLucide" /></IconBox>
          <RadioDot $active={leadSource === 'empty'} aria-hidden>
            {leadSource === 'empty' && <SvgFragment type="checkmark" />}
          </RadioDot>
        </CardTopRow>
        <LeadSourceTitle $active={leadSource === 'empty'}>Custom campaign</LeadSourceTitle>
        <LeadSourceSub>Create a campaign now and add recipients manually later.</LeadSourceSub>
      </LeadSourceCard>
    </LeadSourceGrid>
  </Column>
);

const SectionTitle = styled.span`
  font-family: NunitoSans; font-size: 14px; font-weight: 800; color: ${styles.colors.black600};
`;

const SectionSub = styled.span`
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black400}; line-height: 1.45;
`;

const LeadSourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  @media (max-width: 1280px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 640px) { grid-template-columns: 1fr; gap: 10px; }
`;

const LeadSourceCard = styled.button<{ $active: boolean }>`
  position: relative;
  display: flex; flex-direction: column; align-items: flex-start; gap: 4px;
  padding: 16px 16px; border-radius: 14px; cursor: pointer; text-align: left;
  border: 1px solid ${({ $active }) => $active ? styles.colors.primary600 : '#e1e5eb'};
  background: ${({ $active }) => $active ? 'rgba(89,100,255,0.05)' : '#fff'};
  box-shadow: ${({ $active }) => $active ? '0 1px 3px rgba(0,0,0,0.10), 0 1px 2px rgba(0,0,0,0.10)' : 'none'};
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  &:hover { border-color: ${styles.colors.primary600}; box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.08); }
`;

const CardTopRow = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const IconBox = styled.div<{ $active: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $active }) => $active ? 'rgba(89,100,255,0.10)' : '#e7edff'};
  .svg { width: 20px; height: 20px; }
  .path { stroke: ${styles.colors.primary600}; fill: none; }
`;

const RadioDot = styled.div<{ $active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 999px;
  border: 2px solid ${({ $active }) => $active ? styles.colors.primary600 : '#e1e5eb'};
  background: ${({ $active }) => $active ? styles.colors.primary600 : '#fff'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  .svg { width: 14px; height: 14px; }
  .path { fill: #fff !important; }
`;

const LeadSourceTitle = styled.span<{ $active: boolean }>`
  font-family: NunitoSans; font-size: 14px; font-weight: 800;
  color: ${styles.colors.black600};
`;

const LeadSourceSub = styled.span`
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black400}; line-height: 1.4;
`;

export default CampaignRecipientsSection;
